import { useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import type { DokumentDetail } from "@/lib/dokumente";
import type { EditorPosition } from "@/pages/dokumente/documentPositionModel";
import { buildSaveTemplatePayload } from "@/pages/dokumente/documentTemplateModel";
import { createGdVorlage } from "@/lib/gdVorlagen";
import { ApiError } from "@/lib/apiClient";
import {
  DocumentTemplateMetaFields,
  type DocumentTemplateMetaValue,
} from "@/pages/dokumente/DocumentTemplateMetaFields";

interface DocumentSaveTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  doc: DokumentDetail;
  positions: EditorPosition[];
  onSaved: () => void;
  zIndexClass?: string;
}

export function DocumentSaveTemplateDialog({
  open,
  onClose,
  doc,
  positions,
  onSaved,
  zIndexClass,
}: DocumentSaveTemplateDialogProps) {
  const [name, setName] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [includeHeaderTexts, setIncludeHeaderTexts] = useState(false);
  const [meta, setMeta] = useState<DocumentTemplateMetaValue>({
    schlagworte: [],
    ordner_ids: [],
    sordner_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleClose() {
    if (saving) return;
    setName("");
    setBeschreibung("");
    setIncludeHeaderTexts(false);
    setMeta({ schlagworte: [], ordner_ids: [], sordner_ids: [] });
    setError(null);
    onClose();
  }

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Bitte einen Namen für die Vorlage angeben.");
      return;
    }
    if (positions.length === 0) {
      setError("Die Vorlage braucht mindestens eine Position.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = buildSaveTemplatePayload(doc, positions, {
        name: trimmed,
        beschreibung,
        includeHeaderTexts,
        schlagworte: meta.schlagworte,
        ordner_ids: meta.ordner_ids,
        sordner_ids: meta.sordner_ids,
      });
      await createGdVorlage({
        name: payload.name,
        beschreibung: payload.beschreibung,
        dokument_typ: payload.dokument_typ,
        betreff: payload.betreff,
        einleitung: payload.einleitung,
        schlusstext: payload.schlusstext,
        positionen: payload.positionen,
        schlagworte: payload.schlagworte,
        ordner_ids: payload.ordner_ids,
        sordner_ids: payload.sordner_ids,
      });
      onSaved();
      handleClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Vorlage konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      dirty={name.trim() !== "" || beschreibung.trim() !== "" || meta.schlagworte.length > 0}
      title="Als Vorlage speichern"
      description="Positionen und optional Kopftexte für wiederkehrende Angebote sichern."
      size="lg"
      zIndexClass={zIndexClass}
      footer={
        <>
          <DialogCancelButton onClose={handleClose} disabled={saving} />
          <Button type="button" onClick={() => void handleSave()} loading={saving}>
            Vorlage speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z. B. Türaustausch Standard"
          autoFocus
        />
        <TextInput
          label="Kurzbeschreibung (optional)"
          value={beschreibung}
          onChange={(e) => setBeschreibung(e.target.value)}
          placeholder="Wofür ist diese Vorlage gedacht?"
        />
        <DocumentTemplateMetaFields value={meta} onChange={setMeta} disabled={saving} />
        <label className="flex items-start gap-2 text-sm text-label">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeHeaderTexts}
            onChange={(e) => setIncludeHeaderTexts(e.target.checked)}
          />
          <span>
            <span className="font-medium">Kopftexte mit speichern</span>
            <span className="mt-0.5 block text-label-secondary">
              Betreff, Einleitung und Schlusstext werden in die Vorlage übernommen.
            </span>
          </span>
        </label>
        <p className="text-xs text-label-secondary">
          Es werden {positions.length} Position{positions.length === 1 ? "" : "en"} gespeichert.
        </p>
      </div>
    </Dialog>
  );
}
